import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import iconFileDownload from "../../../assets/media/icon-file-download.svg";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Link } from "react-router-dom";
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import RejectDialog from "./rejectDialog/rejectDialog";
import StatusConfirmDialog, {
  STATUS_LABELS,
} from "./statusConfirmDialog/statusConfirmDialog";
import {
  fetchApplications,
  updateApplication,
  clearApplications,
} from "../../../store/reducers/applicationSlice";
import { createNotification } from "../../../store/reducers/notificationSlice";
import { handleFilesDownload } from "../../../store/reducers/fileSlice";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import iconSearch from "../../../assets/media/icon-search.svg";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import { hasRole, ROLES } from "../../../utility/rbac";
import { getNotificationTemplate } from "../../../utility/notificationTemplates";

const columnHelper = createColumnHelper();

const PendingApp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;
  const applications = useSelector((state) => state.applications);
  const pagination = useSelector((state) => state.applications.pagination);
  const applicationLoading = useSelector((state) => state.applications.fetchLoading);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchBy, setSearchBy] = useState("applicationId");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedRejectItem, setSelectedRejectItem] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  // Clear applications and fetch fresh data when component mounts or filters change
  useEffect(() => {
    // First clear the applications to ensure fresh data
    dispatch(clearApplications());

    // Then fetch applications with current filters
    const fetchData = async () => {
      // Ensure itemsPerPage is a valid positive number
      const validItemsPerPage = (typeof itemsPerPage === 'number' && itemsPerPage > 0) ? itemsPerPage : 10;

      // Use server-side filtering with the correct state parameter
      let queryParams = {
        page: currentPage,
        limit: validItemsPerPage,
        state: 'pending_for_approval',
      };

      if (searchQuery && searchQuery.trim() !== '') {
        queryParams[searchBy] = searchQuery.trim();
      }

      if (startDate) {
        queryParams.createdFrom = moment(startDate).format("YYYY-MM-DD");
      }
      if (endDate) {
        queryParams.createdTo = moment(endDate).format("YYYY-MM-DD");
      }

      await dispatch(fetchApplications({ accessToken: authToken, queryParams }));
    };

    fetchData();
  }, [authToken, dispatch, startDate, endDate, currentPage, searchQuery, itemsPerPage, searchBy]);

  // Merge user data and filter applications
  useEffect(() => {
    // Handle both array and object responses from API
    const applicationsArray = Array.isArray(applications.data)
      ? applications.data
      : applications.data?.applications || applications.data?.data || [];

    if (applicationsArray.length > 0) {
      const mergedData = applicationsArray.map((appItem) => {
        const userItem = getallUserdata?.find?.(
          (userData) => userData.id === appItem.user_id
        );
        if (userItem) {
          return {
            ...appItem,
            applicationId: appItem.id,
            ...userItem,
            userId: userItem.id,
          };
        }
        return { ...appItem, applicationId: appItem.id };
      });

      // Since we're using server-side filtering with state='pending_for_approval',
      // we only need to filter by company_id
      const filteredData = mergedData.filter(
        (item) => {
          const isCorrectCompany = (item.company_id === user.user.company_id || user.user.is_admin || ["reviewer", "viewer"].includes(user.user.user_role?.toLowerCase()));
          return isCorrectCompany;
        }
      );

      setFilteredApplication(filteredData);
    } else {
      setFilteredApplication([]);
    }
  }, [applications.data, getallUserdata, user.user.company_id, user.user.is_admin]);

  const handleSearchInputChange = (e) => {
    let value = e.target.value;
    if (searchBy === "applicationId") {
      value = value.replace(/^gb-?/i, "").replace(/\D/g, "");
    }
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
    setSearchQuery("");
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing items per page
  };

  const getTotalPages = () => {
    // Use server-side pagination data
    if (pagination?.totalPages) {
      return pagination.totalPages;
    }

    // Fallback to 1 if no pagination data
    return 1;
  };

  const handleDownload = async (item) => {
    const { applicationId, userId } = item;
    setDownloadingIds((prev) => [...prev, applicationId]);

    try {
      await dispatch(handleFilesDownload({ userId, applicationId, authToken }));
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setDownloadingIds((prev) => prev.filter((id) => id !== applicationId));
    }
  };

  const handleOpenReject = (item) => {
    setSelectedRejectItem(item);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleCloseReject = () => {
    setShowRejectModal(false);
    setSelectedRejectItem(null);
    setRejectReason("");
  };

  const requestStatusChange = (item, state, discrepancies = null) => {
    setPendingStatusChange({ item, state, discrepancies });
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setPendingStatusChange(null);
  };

  const handleConfirmStatusChange = () => {
    if (!pendingStatusChange) return;
    const { item, state, discrepancies } = pendingStatusChange;
    handleCloseStatusModal();
    handleChangeStatus(item, state, discrepancies);
  };

  const handleConfirmReject = () => {
    const updatedItem = { ...selectedRejectItem };
    const discrepancies =
      selectedRejectItem.discrepancies && typeof selectedRejectItem.discrepancies === "object"
        ? { ...selectedRejectItem.discrepancies }
        : {};
    discrepancies.other = { value: rejectReason, check: true };
    updatedItem.discrepancies = discrepancies;

    setShowRejectModal(false);
    handleChangeStatus(updatedItem, 5, discrepancies);
  };

  const handleChangeStatus = async (item, state, discrepancies = null) => {

    await dispatch(
      updateApplication({
        id: item?.applicationId,
        state: state,
        discrepancies: discrepancies,
        accessToken: authToken,
      })
    ).then(() => {

      // Refetch with proper query parameters to maintain pending filter
      const validItemsPerPage = (typeof itemsPerPage === 'number' && itemsPerPage > 0) ? itemsPerPage : 10;

      let queryParams = {
        page: currentPage,
        limit: validItemsPerPage,
        search: searchQuery,
        state: 'pending_for_approval'
      };

      if (startDate && endDate) {
        queryParams.start_date = startDate.toISOString();
        queryParams.end_date = endDate.toISOString();
      }

      dispatch(fetchApplications({ accessToken: authToken, queryParams }));

      const template = getNotificationTemplate(state, {
        ...item,
        userName: item.userName || item.user_name || 'User',
        discrepancies: discrepancies
      });

      dispatch(createNotification({
        message: template.message,
        userId: item.userId || item.user_id,
        subject: template.subject,
        htmlMailContent: template.html,
        sendToCompanyAdmins: true,
        accessToken: authToken,
        is_admin: false,
      }));
    });
  };

  // Apply client-side pagination since server-side filtering by state doesn't work
  const paginatedData = useMemo(() => {
    const sortedData = filteredApplication.sort((a, b) => b.applicationId - a.applicationId);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const result = sortedData.slice(startIndex, endIndex);
    return result;
  }, [filteredApplication, currentPage, itemsPerPage]);

  // React Table columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * itemsPerPage + info.row.index + 1,
      }),
      columnHelper.accessor("applicationId", {
        header: "Application No.",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.name}</strong>
              <span>{row.category}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedBy",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.companyName || row.company_name || 'N/A'}</strong>
              <span>tanya.hill@example.com</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedFor",
        header: "Submitted For",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.userName || row.user_name || 'N/A'}</strong>
              <span>{row.userEmail || row.user_email || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("updated_at", {
        header: "Submitted Date",
        cell: (info) => <span className="dateText">{info.getValue()?.slice(0, 10) || ""}</span>,
      }),
      columnHelper.display({
        id: "download",
        header: () => <div className="text-center">Download</div>,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              {downloadingIds.includes(row.applicationId) ? (
                <div className="loader"></div>
              ) : (
                <button
                  className="btn btnDownload"
                  type="button"
                  onClick={() => handleDownload(row)}
                >
                  Download
                </button>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              {!hasRole(user, ROLES.VIEWER) && (
                <div className="dropdown d-inline-flex">
                  <button
                    className="application-dropdown dropdown-toggle border-0 bg-transparent"
                    type="button"
                    id="dropdownMenuButton"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <img src={iconMenuDots} alt="Menu Dots" />
                  </button>
                  <ul
                    className="dropdown-menu"
                    aria-labelledby="dropdownMenuButton"
                  >
                    <li>
                      <Link
                        to={`/view-applications/${row.applicationId}`}
                        className="dropdown-item"
                      >
                        View
                      </Link>
                    </li>
                    <li>
                      <Link
                        onClick={() => requestStatusChange(row, 1)}
                        className="dropdown-item"
                      >
                        In Process
                      </Link>
                    </li>
                    <li>
                      <Link
                        onClick={() => requestStatusChange(row, 4)}
                        className="dropdown-item"
                      >
                        Approved
                      </Link>
                    </li>
                    <li>
                      <Link
                        onClick={() => handleOpenReject(row)}
                        className="dropdown-item"
                      >
                        Reject
                      </Link>
                    </li>
                    <li>
                      <Link
                        onClick={() => requestStatusChange(row, 7)}
                        className="dropdown-item"
                      >
                        Renewal
                      </Link>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          );
        },
      }),
    ],
    [downloadingIds, navigate, currentPage, itemsPerPage]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header with search + date filter */}
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Pending For Approval Applications</h2>
            </div>
            <div className="boardHeadRight">
              <div className="">
                <select onChange={handleSearchByChange} value={searchBy}>
                  <option value="applicationId">Search by Application ID</option>
                  <option value="companyName">Search by Company Name</option>
                  <option value="username">Search by Employee Name</option>
                  {/* <option value="email">Search by Email </option> */}
                </select>
              </div>
              <div className="searchForm">
                <input
                  type="text"
                  placeholder={searchBy === "applicationId" ? "GB-XXX" : "Search"}
                  onChange={handleSearchInputChange}
                  value={searchBy === "applicationId" && searchQuery ? `GB-${searchQuery}` : searchQuery}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </div>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [start, end] = update;
                    setStartDate(start);
                    setEndDate(end);
                    setCurrentPage(1);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="yyyy-MM-dd"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="boardContent table-responsive" style={{ position: 'relative' }}>
            {/* Loader overlay for table section */}
            {applicationLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  borderRadius: '4px'
                }}
              >
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 mb-0">Loading applications...</p>
                </div>
              </div>
            )}
            <table className="table">
              <thead className="">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {applicationLoading ? (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr className="" key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      {applicationLoading ? "Loading..." : "Result not found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <RejectDialog
            open={showRejectModal}
            reason={rejectReason}
            setReason={setRejectReason}
            onReject={handleConfirmReject}
            onCancel={handleCloseReject}
          />

          <StatusConfirmDialog
            open={showStatusModal}
            title="Are you sure you want to change status?"
            message={
              pendingStatusChange
                ? `Change status to "${STATUS_LABELS[pendingStatusChange.state] || pendingStatusChange.state}"?`
                : ""
            }
            onConfirm={handleConfirmStatusChange}
            onCancel={handleCloseStatusModal}
          />

          {/* Pagination */}

          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={pagination?.totalItems || filteredApplication.length}
          />

        </div>
      </div>
    </div>
  );
};

export default PendingApp;